import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { videoService } from '../../services/video.service';
import { learningSessionService } from '../../services/learningSession.service';
import './videooverview.css';
// import '../../components/Sidebar.mobile.css'; // ←上書き回避
const normalizeYoutubeId = (raw) => {
  if (!raw) return '';
  try {
    const u = new URL(raw);
    if (u.hostname.includes('youtube.com')) return (u.searchParams.get('v') || '').trim();
    if (u.hostname.includes('youtu.be')) return (u.pathname.replace('/', '') || '').trim();
  } catch {}
  return raw.split('&')[0].trim();
};

const VIDEO_VARIANTS = {
  '64YkJcWh8ns': {
    key: 'beginner-1',
    layout: 'standard',
    accent: '#FF6B6B',
    showAgenda: false,
    showDescription: true,
    badge: '初級',
    banner: 'はじめての生成AI講座',
  },
  '52VJJMYYkMA': {
    key: 'beginner-2',
    layout: 'standard',
    accent: '#8BC34A',
    showAgenda: true,
    showDescription: true,
    badge: '上級',
    banner: '実務で使い倒すテクニック',
  },
  'kk7GlxWZAfk': {
    key: 'beginner-3',
    layout: 'standard',
    accent: '#8BC34A',
    showAgenda: true,
    showDescription: true,
    badge: '上級',
    banner: '実務で使い倒すテクニック',
  },
  default: {
    key: 'default',
    layout: 'standard',
    accent: '#2196F3',
    showAgenda: true,
    showDescription: true,
    badge: null,
    banner: null,
  }
};

// 固定カテゴリ（編集不可）
const CORE_MEMO_CATEGORIES = [
  '未分類', '生成AI', 'ChatGPT', 'マーケティング', '営業', '副業', '目標設計'
];
const DEFAULT_MEMO_CATEGORY = '未分類';
const ADD_OPTION_VALUE = '__ADD__';

export default function VideoLearningPage() {
  const { videoId } = useParams();
  const navigate = useNavigate();

  // モバイルヘッダー（ドロワー開閉）
  const [mhOpen, setMhOpen] = useState(false);
  const toggleMobileHeader = () => setMhOpen(v => !v);
  const closeMobileHeader  = () => setMhOpen(false);

  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [showExtra, setShowExtra] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [videosList, setVideosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ progress_percent: 0, current_time: 0 });
  const [memo, setMemo] = useState('');
  const [progressMap, setProgressMap] = useState({});
  const [query, setQuery] = useState('');
  const [isComposing, setIsComposing] = useState(false);

  // カテゴリ（固定＋カスタム）
  const [customCategories, setCustomCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_MEMO_CATEGORY);
  const [addingInDropdown, setAddingInDropdown] = useState(false);
  const [draftCategory, setDraftCategory] = useState('');
  const selectWrapRef = useRef(null);
  const addInputRef = useRef(null);
  const isComposingRef = useRef(false);

  const allCategories = useMemo(
    () => [...CORE_MEMO_CATEGORIES, ...customCategories],
    [customCategories]
  );

  // 参照
  const progressRef = useRef(progress);
  const currentVideoRef = useRef(currentVideo);
  useEffect(() => { progressRef.current = progress; }, [progress]);
  useEffect(() => { currentVideoRef.current = currentVideo; }, [currentVideo]);

  // ===== Variant 決定 =====
  const presentation = useMemo(() => {
    if (currentVideo?.presentation) {
      return { ...VIDEO_VARIANTS.default, ...currentVideo.presentation };
    }
    if (currentVideo?.id && VIDEO_VARIANTS[currentVideo.id]) {
      return { ...VIDEO_VARIANTS.default, ...VIDEO_VARIANTS[currentVideo.id] };
    }
    return VIDEO_VARIANTS.default;
  }, [currentVideo?.id, currentVideo?.presentation]);

  const styleVars = useMemo(() => ({ '--accent': presentation.accent }), [presentation.accent]);

  // ===== ユーザー定義カテゴリ 取得/保存 =====
  const loadUserCategories = async () => {
    try {
      if (videoService.getUserMemoCategories) {
        const list = await videoService.getUserMemoCategories();
        if (Array.isArray(list)) {
          setCustomCategories(list);
          return;
        }
      }
    } catch {}
    const ls = localStorage.getItem('userMemoCategories');
    if (ls) setCustomCategories(JSON.parse(ls));
  };

  const persistUserCategories = async (next) => {
    try {
      if (videoService.saveUserMemoCategories) {
        await videoService.saveUserMemoCategories(next);
      } else {
        localStorage.setItem('userMemoCategories', JSON.stringify(next));
      }
    } catch {
      localStorage.setItem('userMemoCategories', JSON.stringify(next));
    }
  };

  useEffect(() => {
    document.body.classList.add('page--videooverview');
    return () => document.body.classList.remove('page--videooverview');
  }, []);

  useEffect(() => { loadUserCategories(); }, []);

  const norm = (s) => s.replace(/\s+/g, '').toLowerCase();

  const addCustomCategory = async (nameRaw) => {
    const name = (nameRaw ?? draftCategory).trim();
    if (!name) return;

    const exists =
      CORE_MEMO_CATEGORIES.some(c => norm(c) === norm(name)) ||
      customCategories.some(c => norm(c) === norm(name));

    if (exists) { alert('同名のカテゴリーが既にあります'); return; }

    const next = [...customCategories, name];
    setCustomCategories(next);
    await persistUserCategories(next);
    setSelectedCategory(name);
    setDraftCategory('');
  };

  const removeCustomCategory = async (name) => {
    const next = customCategories.filter(c => c !== name);
    setCustomCategories(next);
    await persistUserCategories(next);
    if (selectedCategory === name) setSelectedCategory(DEFAULT_MEMO_CATEGORY);
  };

  const loadMemoCategory = async (vid) => {
    let cat = DEFAULT_MEMO_CATEGORY;
    try {
      if (videoService.getMemoCategory) {
        const c = await videoService.getMemoCategory(vid);
        if (c) cat = c;
      } else {
        const ls = localStorage.getItem(`memoCategory:${vid}`);
        if (ls) cat = ls;
      }
    } catch {
      const ls = localStorage.getItem(`memoCategory:${vid}`);
      if (ls) cat = ls;
    }

    if (cat && !allCategories.includes(cat)) {
      const next = [...customCategories, cat];
      setCustomCategories(next);
      await persistUserCategories(next);
    }
    setSelectedCategory(cat || DEFAULT_MEMO_CATEGORY);
  };

  const saveMemoCategory = async (vid, cat) => {
    try {
      if (videoService.saveMemoCategory) {
        await videoService.saveMemoCategory(vid, cat);
      } else {
        localStorage.setItem(`memoCategory:${vid}`, cat);
      }
    } catch {
      localStorage.setItem(`memoCategory:${vid}`, cat);
    }
  };

  // 共有
  const handleSharePage = async () => {
    const url = window.location.href;
    const title = currentVideo?.title || document.title;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        alert('ページのURLをコピーしました');
      } else {
        window.prompt('このURLをコピーしてください:', url);
      }
    } catch {}
  };

  // 初期化
  useEffect(() => {
    const initializeVideoData = async () => {
      try {
        setLoading(true);
        setError(null);

        const videos = await videoService.getVideosList({ limit: 100 });
        if (videos?.length) {
          setVideosList(videos);

          const videoIds = videos.map(v => v.id);
          const allProgress = await videoService.getMultipleProgress(videoIds);
          setProgressMap(allProgress);

          const targetId = normalizeYoutubeId(videoId || videos[0].id);
          const details = await videoService.getVideoDetails(targetId);
          setCurrentVideo(details);

          const p = await videoService.getProgress(targetId);
          setProgress(p);

          const m = await videoService.getMemo(targetId);
          setMemo(m);

          await loadMemoCategory(targetId);
          await learningSessionService.startVideoSession(targetId);

          const idx = videos.findIndex(v => v.id === targetId);
          if (idx !== -1) setSelectedVideoIndex(idx);
        } else {
          setError('ローカル環境では動画データがありません。本番環境をご利用ください。');
        }
      } catch (err) {
        console.error('Error initializing video data:', err);
        setError('動画データの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    initializeVideoData();
  }, [videoId]); // eslint-disable-line react-hooks/exhaustive-deps

  // YouTube
  useEffect(() => {
    if (!currentVideo?.youtube_id && !currentVideo?.id) return;

    let intervalId = null;
    let isPlayerReady = false;

    const initializePlayer = () => {
      if (window.player?.destroy) {
        try { window.player.destroy(); window.player = null; } catch (e) {}
      }
      const playerContainer = document.getElementById('youtube-player');
      if (playerContainer) playerContainer.innerHTML = '';

      const ytId = normalizeYoutubeId(currentVideo?.youtube_id || currentVideo?.id);
      window.player = new window.YT.Player('youtube-player', {
        videoId: ytId,
        playerVars: { rel: 0, modestbranding: 1, enablejsapi: 1 },
        events: { onReady: onPlayerReady, onStateChange: onPlayerStateChange }
      });
    };

    if (window.YT?.Player) {
      initializePlayer();
    } else {
      if (!window.youtubeApiLoading) {
        window.youtubeApiLoading = true;
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(script);
      }
      window.onYouTubeIframeAPIReady = () => {
        window.youtubeApiLoading = false;
        if (currentVideoRef.current?.id) initializePlayer();
      };
    }

    function onPlayerReady() {
      isPlayerReady = true;
      const savedTime = progressRef.current?.current_time || 0;
      if (savedTime > 0) {
        setTimeout(() => { window.player?.seekTo?.(savedTime); }, 1000);
      }

      intervalId = setInterval(async () => {
        if (!window.player || !isPlayerReady) return;
        try {
          const currentTime = window.player.getCurrentTime();
          const duration = window.player.getDuration();
          if (isNaN(currentTime) || isNaN(duration) || duration <= 0) return;

          const percent = Math.floor((currentTime / duration) * 100);
          const vid = currentVideoRef.current?.id;
          if (!vid) return;

          await videoService.recordProgress(vid, currentTime, duration);

          if (learningSessionService.hasActiveSession(vid)) {
            await learningSessionService.updateVideoSession(vid, currentTime);
          }

          setProgress({ progress_percent: percent, current_time: currentTime });
          setProgressMap(prev => ({ ...prev, [vid]: { progress_percent: percent, current_time: currentTime } }));
        } catch (error) {
          console.error('Error updating progress:', error);
        }
      }, 5000);
    }

    function onPlayerStateChange(event) {
      const vid = currentVideoRef.current?.id;
      if (!vid) return;

      // YouTube Player States: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
      if (event.data === 1) { // Playing
        console.log('🎬 Video started playing:', vid);
        if (learningSessionService.hasActiveSession(vid)) {
          learningSessionService.resumeVideoSession(vid);
        }
      } else if (event.data === 2) { // Paused
        console.log('⏸️ Video paused:', vid);
        if (learningSessionService.hasActiveSession(vid)) {
          learningSessionService.pauseVideoSession(vid);
        }
      } else if (event.data === 0) { // Ended
        console.log('🏁 Video ended:', vid);
        if (learningSessionService.hasActiveSession(vid)) {
          learningSessionService.endVideoSession(vid);
        }
      }
    }

    return () => {
      isPlayerReady = false;
      if (intervalId) clearInterval(intervalId);
      const vid = currentVideoRef.current?.id;
      if (vid) learningSessionService.endVideoSession(vid);
    };
  }, [currentVideo?.youtube_id, currentVideo?.id]);

  // 再生位置復元
  useEffect(() => {
    const vid = currentVideoRef.current?.id;
    if (!vid) return;
    if (window.player?.seekTo && progress.current_time > 0) {
      const now = window.player.getCurrentTime ? window.player.getCurrentTime() : 0;
      if (Math.abs(now - progress.current_time) > 5) {
        setTimeout(() => { window.player?.seekTo?.(progress.current_time); }, 500);
      }
    }
  }, [progress.current_time]);

  // アクセシビリティ（動画リスト）
  useEffect(() => {
    const videoItems = document.querySelectorAll('.video-item');
    videoItems.forEach((item, index) => {
      item.setAttribute('tabindex', '0');
      item.setAttribute('role', 'button');
      item.setAttribute('aria-label', `動画 ${index + 1} を選択`);
    });
  }, [videosList]);

  // もっと見る 表示切替
  useEffect(() => {
    const showBtn = document.querySelector('.show-more-btn');
    const closeBtn = document.querySelector('.close-extra-btn');
    const extra = document.querySelector('.video-extra');
    const isMobile = () => window.innerWidth <= 768;

    if (showBtn && closeBtn && extra) {
      const updateDisplay = () => {
        if (isMobile()) {
          extra.style.display = showExtra ? 'block' : 'none';
          showBtn.style.display = showExtra ? 'none' : 'inline-block';
        } else {
          extra.style.display = 'block';
          showBtn.style.display = 'none';
        }
      };
      updateDisplay();
      window.addEventListener('resize', updateDisplay);

      showBtn.setAttribute('aria-label', '動画の詳細情報を表示');
      closeBtn.setAttribute('aria-label', '動画の詳細情報を閉じる');
      showBtn.setAttribute('tabindex', '0');
      closeBtn.setAttribute('tabindex', '0');
    }
  }, [showExtra]);

  // アンマウント
  useEffect(() => {
    const handleBeforeUnload = () => {
      learningSessionService.endAllSessions();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      try { window.player?.destroy?.(); window.player = null; } catch {}
      learningSessionService.endAllSessions();
    };
  }, []);

  // モバイル：ESC で閉じる & オーバースクロール防止
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeMobileHeader(); };
    window.addEventListener('keydown', onKey);
    document.documentElement.classList.toggle('no-scroll', mhOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.documentElement.classList.remove('no-scroll');
    };
  }, [mhOpen]);

  // 検索（右のリスト絞り込み）
  const filteredVideos = videosList.filter(v =>
    !query || (v.title || '').toLowerCase().includes(query.toLowerCase())
  );
  const listForRender = query ? filteredVideos : videosList;

  // ▼ プルダウンの選択変更
  const handleCategoryChange = (e) => {
    const val = e.target.value;
    if (val === ADD_OPTION_VALUE) {
      setAddingInDropdown(true);
      setDraftCategory('');
      setTimeout(() => addInputRef.current?.focus(), 0);
      e.target.value = selectedCategory; // 表示は直前の選択に戻す
      return;
    }
    setSelectedCategory(val);
  };

  // ▼ 外側クリックでポップオーバー閉じる
  useEffect(() => {
    if (!addingInDropdown) return;
    const onDocClick = (ev) => {
      if (!selectWrapRef.current) return;
      if (!selectWrapRef.current.contains(ev.target)) {
        setAddingInDropdown(false);
        setDraftCategory('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [addingInDropdown]);

  // ▼ 追加確定
  const confirmAddCategory = async () => {
    const name = draftCategory.trim();
    if (!name) return;
    await addCustomCategory(name);
    setAddingInDropdown(false);
  };

  return (
    <div className={`main-layout page--videooverview layout-${presentation.layout}`} style={styleVars}>
      <div className="fixed-bg"></div>

      {/* ▼ モバイルヘッダー */}
      <header className="mobile-header" role="banner">
        <button
          className={`mh-burger ${mhOpen ? 'active' : ''}`}
          aria-label={mhOpen ? 'メニューを閉じる' : 'メニューを開く'}
          aria-expanded={mhOpen}
          onClick={toggleMobileHeader}
        >
          <span></span><span></span><span></span>
        </button>

        <Link to="/dashboard" className="mh-logo" onClick={closeMobileHeader} aria-label="トップへ">
          <img src="/logo-2.png" alt="ロゴ" />
        </Link>
      </header>

      {/* バックドロップ */}
      <div
        className={`mh-backdrop ${mhOpen ? 'show' : ''}`}
        onClick={closeMobileHeader}
        aria-hidden="true"
      />

      {/* ▼ モバイル用サイドバー（ドロワー） */}
      <aside className={`sidebar left drawer ${mhOpen ? 'open' : ''}`} aria-hidden={!mhOpen}>
        <div className="dashboard-logo sb-drawer-brand">
          <Link to="/dashboard" onClick={closeMobileHeader}>
            <img src="/logo-2.png" alt="ロゴ" />
          </Link>
        </div>
        <nav onClick={closeMobileHeader}>
          <li>
            <Link to="/dashboard" className="nav-link">
              <img src="/all.png" alt="概要" className="nav-icon" />
              概要
            </Link>
          </li>
          <li>
            <Link to="/videolist" className="nav-link">
              <img src="/videos.png" alt="動画" className="nav-icon" />
              動画
            </Link>
          </li>
          <li>
            <Link to="/memo" className="nav-link">
              <img src="/memo.png" alt="メモ" className="nav-icon" />
              メモ
            </Link>
          </li>
          <li>
            <Link to="/setting" className="nav-link">
              <img src="/setting.png" alt="設定" className="nav-icon" />
              設定
            </Link>
          </li>
        </nav>
      </aside>

      {/* 左：動画エリア */}
      <div className="video-area">
        <div className="page-header">
          {/* 768px以下ではCSSでこのロゴは非表示にする */}
          <Link to="/dashboard" className="videos-logo">
            <img src={require('./logo.png')} alt="logo" />
          </Link>
          <Link to="/videolist" className="back-link" tabIndex="0" aria-label="前のページに戻る">
            ＜戻る
          </Link>
        </div>

        <div className={`video-player ${presentation.layout === 'compact' ? 'player-compact' : ''}`}>
          <div className="yt-top-right-mask" aria-hidden="true"></div>

          <button type="button" className="share-copy-btn" onClick={handleSharePage}>
            <img src="/link.png" alt="" className="share-copy-icon" />
          </button>

          <div id="youtube-player" tabIndex="0" aria-label="YouTube動画プレイヤー"></div>
        </div>

        <div className="video-box">
          <div className="video-text">
            <p className="upload-date">
              {currentVideo
                ? new Date(currentVideo.created_at).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' })
                : '読み込み中...'}
            </p>
            <h2 className="video-title-1">
              {currentVideo ? currentVideo.title : '動画を読み込み中...'}
            </h2>

            {(presentation.showAgenda || presentation.showDescription) && (
              <div className="video-extra" style={{ display: showExtra || window.innerWidth > 768 ? 'block' : 'none' }}>
                <button
                  className="close-extra-btn"
                  onClick={() => setShowExtra(false)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowExtra(false); } }}
                  tabIndex="0"
                  aria-label="詳細情報を閉じる"
                >
                  ✕
                </button>

                {presentation.showAgenda && (
                  <div className="video-agenda" role="region" aria-label="動画目次">
                    <p>▼動画目次</p>
                    <ul>
                      {currentVideo?.agenda?.length ? (
                        currentVideo.agenda.map((item, index) => (
                          <li key={index} tabIndex="0">{item.time}　{item.title}</li>
                        ))
                      ) : (
                        <li tabIndex="0">目次情報を読み込み中...</li>
                      )}
                    </ul>
                  </div>
                )}

                {presentation.showDescription && (
                  <div className="video-text">
                    <p>▼概要</p>
                    <div className="video-description">
                      {currentVideo ? <p>{currentVideo.description}</p> : <p>概要を読み込み中...</p>}
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              className="show-more-btn"
              onClick={() => setShowExtra(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowExtra(true); } }}
              tabIndex="0"
              aria-label="動画の詳細情報を表示"
            >
              もっと見る
            </button>
          </div>
        </div>
      </div>

      {/* 右カラム：検索バー + サイドパネル */}
      <aside className="right-column">
        <div className="side-search">
          <input
            type="text"
            className="side-search-input"
            placeholder="動画を検索する"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="動画検索"
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isComposing) {
                navigate("/videolist");
              }  
            }}
          />
        </div>

        <div className="side-panel">
          <div className="video-list" role="region" aria-label="動画リスト">
            {loading ? (
              <div className="loading-state">動画一覧を読み込み中...</div>
            ) : error ? (
              <div className="error-state">エラー: {error}</div>
            ) : (
              (query ? filteredVideos : videosList).map((video, i) => {
                const isSelected = selectedVideoIndex === i;
                const handleVideoSelect = async () => {
                  try {
                    if (currentVideoRef.current) {
                      await learningSessionService.endVideoSession(currentVideoRef.current.id);
                    }
                    setSelectedVideoIndex(i);

                    const pureId = normalizeYoutubeId(video.id);
                    const details = await videoService.getVideoDetails(pureId);
                    setCurrentVideo(details);

                    const p = await videoService.getProgress(pureId);
                    setProgress(p);

                    const m = await videoService.getMemo(pureId);
                    setMemo(m);

                    await loadMemoCategory(pureId);
                    await learningSessionService.startVideoSession(pureId);
                  } catch (err) {
                    console.error('Error selecting video:', err);
                    setError('動画の詳細情報を取得できませんでした');
                  }
                };

                return (
                  <div
                    className={`video-item ${isSelected ? 'selected' : ''}`}
                    data-video-id={video.id}
                    data-video-index={i}
                    key={video.id}
                    tabIndex="0"
                    role="button"
                    aria-label={`動画 ${i + 1}: ${video.title} - ${video.formatted_duration}`}
                    onClick={handleVideoSelect}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleVideoSelect(); } }}
                  >
                    <div className="video-header">
                      <div className="video-icon">
                        {video.thumbnail_url ? (
                          <img src={video.thumbnail_url} alt="動画サムネイル" className="video-thumbnail" />
                        ) : (
                          <img src={require('./video-icon.png')} alt="再生アイコン" />
                        )}
                      </div>
                      <div className="video-title">{video.title}</div>
                    </div>
                    <div className="video-time">{video.formatted_duration}</div>
                    <div className="video-progress-wrapper">
                      <progress className="video-progress" value={progressMap[video.id]?.progress_percent || 0} max="100"></progress>
                      <span className="video-percent">{progressMap[video.id]?.progress_percent || 0}%</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="memo-box" role="region" aria-label="メモ欄">
            <div className="memo-container">
              <div className="memo-header">
                <h2 className="memo-title">MEMO</h2>

                <div className="memo-actions" ref={selectWrapRef}>
                  <label htmlFor="memo-category" className="sr-only">メモカテゴリー</label>
                  <div className="select-wrap">
                    <select
                      id="memo-category"
                      className="memo-category"
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      aria-label="メモカテゴリーを選択"
                    >
                      <optgroup label="固定（編集不可）">
                        {CORE_MEMO_CATEGORIES.map(c => (
                          <option key={`core-${c}`} value={c}>{c}</option>
                        ))}
                      </optgroup>
                      <optgroup label="カスタム">
                        <option value={ADD_OPTION_VALUE}>＋ 新規カテゴリ…</option>
                        {customCategories.map(c => (
                          <option key={`custom-${c}`} value={c}>{c}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {/* 追加ポップオーバー */}
                  {addingInDropdown && (
                    <div className="inline-add-popover" role="dialog" aria-label="新規カテゴリを追加">
                      <input
                        ref={addInputRef}
                        type="text"
                        className="memo-category-input"
                        placeholder="カテゴリ名を入力"
                        value={draftCategory}
                        onChange={(e) => setDraftCategory(e.target.value)}
                        onCompositionStart={() => (isComposingRef.current = true)}
                        onCompositionEnd={() => (isComposingRef.current = false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !isComposingRef.current) {
                            e.preventDefault();
                            confirmAddCategory();
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setAddingInDropdown(false);
                            setDraftCategory('');
                          }
                        }}
                        aria-label="新規カテゴリ名"
                      />
                      <div className="popover-actions">
                        <button
                          type="button"
                          className="btn-cancel"
                          onClick={() => { setAddingInDropdown(false); setDraftCategory(''); }}
                        >
                          キャンセル
                        </button>
                        <button
                          type="button"
                          className="btn-add"
                          onClick={confirmAddCategory}
                        >
                          追加
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    className="memo-save-btn"
                    aria-label="メモを保存"
                    onClick={async () => {
                      const vid = currentVideoRef.current?.id;
                      if (!vid) return;
                      try {
                        await videoService.saveMemo(vid, memo);
                        await saveMemoCategory(vid, selectedCategory);
                        alert('メモとカテゴリーが保存されました');
                      } catch (error) {
                        console.error('Error saving memo:', error);
                        alert('メモの保存に失敗しました');
                      }
                    }}
                  >
                    保存
                  </button>
                </div>
              </div>

              {!!customCategories.length && (
                <div className="memo-category-chips" aria-label="カスタムカテゴリ一覧">
                  {customCategories.map(c => (
                    <span key={`chip-${c}`} className="memo-chip">
                      {c}
                      <button
                        type="button"
                        className="memo-chip-delete"
                        aria-label={`${c} を削除`}
                        onClick={() => removeCustomCategory(c)}
                        title="削除"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <p className="memo-note" role="note" aria-live="polite">
                1つの動画につきメモ・タグは1個のみとなります
              </p>

              <textarea
                className="memo-textarea"
                placeholder="ここにメモを記入しましょう"
                tabIndex="0"
                aria-label="動画メモを入力"
                rows="8"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              ></textarea>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
